<template>
  <div class="mentoring-training-page">
    <WeCircularNavbar />

    <section class="page-section">
      <div class="page-container">
        <h1 class="page-title">Mentoring and Training Catalogues</h1>

        <p class="page-intro">
          Access tailored mentoring and training opportunities designed to build skills, strengthen capacities and
          support sustainable growth and innovation targeting women entrepreneurs.
        </p>

        <div class="filters">
          <div class="search-box">
            <span class="search-box__icon">⌕</span>
            <input v-model.trim="searchTerm" type="text" class="search-box__input" placeholder="Search catalogues" />
          </div>

          <select v-model="selectedCountry" class="filter-select">
            <option value="">Select Country</option>
            <option v-for="country in countries" :key="country" :value="country">
              {{ country }}
            </option>
          </select>
        </div>

        <div v-if="paginatedItems.length" class="cards-list">
          <article v-for="item in paginatedItems" :key="item.name + item.countryRegion" class="catalogue-card">
            <h2 class="catalogue-card__title">{{ item.name }}</h2>

            <p v-if="item.description" class="catalogue-card__description">
              {{ item.description }}
            </p>

            <div class="catalogue-card__meta">
              <div class="meta-item">
                <span class="meta-label">Country/Region:</span>
                <span class="meta-value">{{ item.countryRegion || '-' }}</span>
              </div>

              <div class="meta-item">
                <span class="meta-label">Website:</span>
                <a :href="formatUrl(item.website)" target="_blank" rel="noopener noreferrer" class="meta-link">
                  {{ websiteLabel(item.website) }}
                </a>
              </div>

              <div class="meta-item">
                <span class="meta-label">Contact:</span>
                <span class="meta-value">{{ item.contact || '-' }}</span>
              </div>
            </div>

            <a
              v-if="item.website"
              :href="formatUrl(item.website)"
              target="_blank"
              rel="noopener noreferrer"
              class="learn-more"
            >
              Learn More
              <span aria-hidden="true">→</span>
            </a>
          </article>
        </div>

        <div v-else class="empty-state">
          No catalogue entries found.
        </div>

        <div v-if="totalPages > 1" class="pagination-dots">
          <button
            v-for="page in totalPages"
            :key="'page-' + page"
            type="button"
            class="pagination-dot"
            :class="{ 'pagination-dot--active': currentPage === page }"
            @click="currentPage = page"
          ></button>
        </div>
      </div>
    </section>

    <WeCircularFooter />
  </div>
</template>

<script>
import WeCircularNavbar from './WeCircularNavbar.vue'
import WeCircularFooter from './WeCircularFooter.vue'
import { mentoringTrainingCataloguesData } from './mentoringTrainingCataloguesData.js'

export default {
  name: 'MentoringAndTrainingCataloguesPage',
  components: {
    WeCircularNavbar,
    WeCircularFooter
  },
  data() {
    return {
      searchTerm: '',
      selectedCountry: '',
      currentPage: 1,
      itemsPerPage: 6
    }
  },
  computed: {
    allItems() {
      if (!mentoringTrainingCataloguesData || typeof mentoringTrainingCataloguesData !== 'object') {
        return []
      }

      return Object.values(mentoringTrainingCataloguesData).flat()
    },
    countries() {
      return [...new Set(this.allItems.map(item => item.countryRegion).filter(Boolean))].sort()
    },
    filteredItems() {
      const term = this.searchTerm.toLowerCase()

      return this.allItems.filter(item => {
        const matchesSearch =
          !term ||
          [item.name, item.description, item.countryRegion, item.website, item.contact].some(value =>
            String(value || '')
              .toLowerCase()
              .includes(term)
          )

        const matchesCountry = !this.selectedCountry || item.countryRegion === this.selectedCountry

        return matchesSearch && matchesCountry
      })
    },
    totalPages() {
      return Math.max(1, Math.ceil(this.filteredItems.length / this.itemsPerPage))
    },
    paginatedItems() {
      const start = (this.currentPage - 1) * this.itemsPerPage
      return this.filteredItems.slice(start, start + this.itemsPerPage)
    }
  },
  watch: {
    searchTerm() {
      this.currentPage = 1
    },
    selectedCountry() {
      this.currentPage = 1
    }
  },
  methods: {
    formatUrl(url) {
      if (!url) {
        return '#'
      }

      if (/^https?:\/\//i.test(url)) {
        return url
      }

      if (/\.pdf$/i.test(url)) {
        return url
      }

      return `https://${url}`
    },
    websiteLabel(url) {
      if (!url) {
        return '-'
      }

      if (/\.pdf$/i.test(url)) {
        return '[pdf] open file'
      }

      return url.replace(/^https?:\/\//i, '').replace(/\/$/, '')
    }
  }
}
</script>

<style scoped>
.mentoring-training-page {
  min-height: 100vh;
  background: #f4f7f8;
  text-align: left;
}

.page-section {
  width: 100%;
}

.page-container {
  width: 100%;
  max-width: 1240px;
  margin: 0 auto;
  padding: 40px 32px 64px;
  box-sizing: border-box;
}

.page-title {
  margin: 0 0 14px;
  font-size: 42px;
  line-height: 1.12;
  font-weight: 700;
  color: #0f4c97;
  text-align: left;
}

.page-intro {
  max-width: 760px;
  margin: 0 0 26px;
  font-size: 18px;
  line-height: 1.7;
  color: #4f6f8f;
  text-align: left;
}

.filters {
  display: grid;
  grid-template-columns: minmax(220px, 1.3fr) minmax(180px, 1fr);
  gap: 14px;
  margin-bottom: 24px;
}

.search-box,
.filter-select {
  min-height: 40px;
  border: 1px solid #b7d9d6;
  border-radius: 999px;
  background: #eef8f7;
  color: #245066;
  box-sizing: border-box;
}

.search-box {
  display: flex;
  align-items: center;
  padding: 0 14px;
}

.search-box__icon {
  margin-right: 10px;
  color: #6a8a89;
}

.search-box__input {
  width: 100%;
  height: 38px;
  border: none;
  outline: none;
  background: transparent;
  color: #245066;
  font-size: 15px;
}

.filter-select {
  padding: 0 14px;
  outline: none;
}

.cards-list {
  display: grid;
  gap: 14px;
}

.catalogue-card {
  padding: 20px 22px;
  border-radius: 10px;
  background: #0f6668;
  box-shadow: 0 8px 20px rgba(8, 69, 73, 0.1);
}

.catalogue-card__title {
  margin: 0 0 10px;
  font-size: 20px;
  line-height: 1.3;
  font-weight: 700;
  color: #ffffff;
}

.catalogue-card__description {
  margin: 0 0 14px;
  font-size: 15px;
  line-height: 1.65;
  color: rgba(255, 255, 255, 0.92);
  text-align: left;
}

.catalogue-card__meta {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 12px;
  padding-top: 14px;
  border-top: 1px solid rgba(255, 255, 255, 0.16);
}

.meta-item {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.meta-label {
  color: rgba(209, 241, 239, 0.84);
  font-size: 12px;
  line-height: 1.2;
  font-weight: 600;
}

.meta-value,
.meta-link {
  color: #ffffff;
  font-size: 14px;
  line-height: 1.5;
  word-break: break-word;
  text-decoration: none;
}

.meta-link:hover {
  text-decoration: underline;
}

.learn-more {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  margin-top: 14px;
  color: #d7f8f4;
  text-decoration: none;
  font-weight: 700;
  line-height: 1.2;
}

.learn-more:hover {
  text-decoration: underline;
}

.empty-state {
  padding: 28px 20px;
  border-radius: 12px;
  background: #ffffff;
  color: #4f6f8f;
  font-size: 16px;
}

.pagination-dots {
  display: flex;
  justify-content: center;
  gap: 8px;
  margin-top: 18px;
}

.pagination-dot {
  width: 8px;
  height: 8px;
  border: none;
  border-radius: 50%;
  background: #cbd7db;
  cursor: pointer;
  transition: background 0.2s ease, transform 0.2s ease;
}

.pagination-dot--active {
  background: #18baa8;
  transform: scale(1.15);
}

@media (max-width: 900px) {
  .filters {
    grid-template-columns: 1fr;
  }

  .catalogue-card__meta {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 768px) {
  .page-container {
    padding: 30px 20px 48px;
  }

  .page-title {
    font-size: 30px;
  }

  .page-intro {
    font-size: 17px;
  }

  .catalogue-card {
    padding: 18px 16px;
  }

  .catalogue-card__title {
    font-size: 18px;
  }

  .catalogue-card__description,
  .meta-value,
  .meta-link {
    font-size: 14px;
  }
}
</style>
